<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useSanityContentTemplates } from 'dashboard/composables/useSanityContentTemplates';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const emit = defineEmits(['onSelect']);

const { t } = useI18n();
const store = useStore();
const query = ref('');
const isRefreshing = ref(false);

const twilioTemplates = computed(() => {
  const inbox = store.getters['inboxes/getInbox'](props.inboxId);
  return inbox?.content_templates?.templates || [];
});

// digitt: DENG-7204 — Sanity-curated template metadata (see useSanityContentTemplates).
const { sanityTemplatesById, loadFromCache, fetchTemplates, templateTitle } =
  useSanityContentTemplates();

onMounted(loadFromCache);

// digitt: DENG-7204 — approved Twilio templates indexed by Content SID for lookup
const approvedTemplatesByContentSid = computed(() => {
  const map = new Map();
  twilioTemplates.value.forEach(template => {
    if (template.status === 'approved') map.set(template.content_sid, template);
  });
  return map;
});

// digitt: DENG-7204 — iterate the Sanity list in its own order (mirrors the Front extension's
// render order), keeping only entries that have an approved Twilio template and match the search.
// Category order follows first appearance in Sanity; within a category, Sanity order is preserved.
const groupedTemplates = computed(() => {
  const groups = {};
  if (!sanityTemplatesById.value) return groups;
  sanityTemplatesById.value.forEach(sanity => {
    const template = approvedTemplatesByContentSid.value.get(sanity.templateId);
    if (!template) return;
    const title = sanity.templateName || template.friendly_name;
    if (!title.toLowerCase().includes(query.value.toLowerCase())) return;
    const category = sanity.category || 'utility';
    if (!groups[category]) groups[category] = [];
    groups[category].push(template);
  });
  return groups;
});

const hasResults = computed(
  () => Object.keys(groupedTemplates.value).length > 0
);

const refreshTemplates = async () => {
  isRefreshing.value = true;
  try {
    // digitt: DENG-7204 — refresh is the only place we hit Sanity + Twilio
    await Promise.all([
      fetchTemplates(),
      store.dispatch('inboxes/syncTemplates', props.inboxId),
    ]);
    useAlert(t('CONTENT_TEMPLATES.PICKER.REFRESH_SUCCESS'));
  } catch (error) {
    useAlert(t('CONTENT_TEMPLATES.PICKER.REFRESH_ERROR'));
  } finally {
    isRefreshing.value = false;
  }
};
</script>

<template>
  <div class="w-full">
    <div class="flex gap-2 mb-2.5">
      <div
        class="flex flex-1 gap-1 items-center px-2.5 py-0 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus-within:outline-n-brand dark:focus-within:outline-n-brand"
      >
        <fluent-icon icon="search" class="text-n-slate-12" size="16" />
        <!-- digitt: DENG-7596 — data-magical-* attrs opt this input out of the Magical extension's text expansion -->
        <input
          v-model="query"
          type="search"
          :placeholder="t('CONTENT_TEMPLATES.PICKER.SEARCH_PLACEHOLDER')"
          class="reset-base w-full h-9 bg-transparent text-n-slate-12 !text-sm !outline-0"
          data-magical-prevent-expansion
          data-magical-prevent-dropdown
        />
      </div>
      <button
        :disabled="isRefreshing"
        class="flex justify-center items-center w-9 h-9 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 hover:bg-n-alpha-2 dark:hover:bg-n-solid-2 disabled:opacity-50 disabled:cursor-not-allowed"
        :title="t('CONTENT_TEMPLATES.PICKER.REFRESH_BUTTON')"
        @click="refreshTemplates"
      >
        <Icon
          icon="i-lucide-refresh-ccw"
          class="text-n-slate-12 size-4"
          :class="{ 'animate-spin': isRefreshing }"
        />
      </button>
    </div>
    <div class="max-h-[25rem] overflow-y-auto">
      <!-- digitt: DENG-7204 — templates grouped by Sanity category; Sanity title + one-line body subtitle -->
      <div v-for="(templates, category) in groupedTemplates" :key="category">
        <p
          class="pb-3 mt-3 mb-3 text-xs font-medium uppercase border-b border-n-slate-6 text-n-slate-11"
        >
          {{ category }}
        </p>
        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="template in templates"
            :key="template.content_sid"
            class="p-2.5 text-left rounded-lg cursor-pointer bg-n-slate-9/10 outline outline-1 outline-n-container hover:bg-n-slate-9/20"
            @click="emit('onSelect', template)"
          >
            <p class="text-sm truncate">{{ templateTitle(template) }}</p>
          </button>
        </div>
      </div>
      <div v-if="!hasResults" class="py-8 text-center">
        <div v-if="query && twilioTemplates.length">
          <p>
            {{ t('CONTENT_TEMPLATES.PICKER.NO_TEMPLATES_FOUND') }}
            <strong>{{ query }}</strong>
          </p>
        </div>
        <div v-else-if="!twilioTemplates.length" class="space-y-4">
          <p class="text-n-slate-11">
            {{ t('CONTENT_TEMPLATES.PICKER.NO_TEMPLATES_AVAILABLE') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
